/**
 * `canary auth doctor` — print everything a user or IT admin needs to see
 * when the CLI is misbehaving against a corporate proxy (Zscaler, Netskope,
 * ZPA, Cloudflare Access). Reads the stored auth, decodes the JWT locally,
 * and probes a curated set of endpoints so we can tell apart:
 *   - TLS interception                 (Node doesn't trust the proxy CA)
 *   - URL-category filtering           (some paths fail, others pass)
 *   - Proxy-injected block pages       (HTML body / missing request-id header)
 *   - Real auth failures               (401 with our header present)
 */

import process from 'node:process';
import {
  getArgValue,
  readStoredAuth,
  readAllProfiles,
  findSessionByOrg,
  getOrgSelector,
  printAmbiguousOrg,
  printNoSessionForOrg,
  envToProfile,
  ENV_URLS,
} from './auth.js';
import { DEFAULT_API_URL } from './default-urls.js';
import { decodeJwtPayload } from './jwt.js';
import { apiFetch } from './api-fetch.js';
import { readBrandEnv } from './brand.js';

interface Probe {
  name: string;
  path: string;
  method?: string;
}

const DEFAULT_PROBES: Probe[] = [
  { name: 'GET /me', path: '/me' },
  { name: 'GET /org/credentials', path: '/org/credentials' },
  { name: 'GET /org/properties', path: '/org/properties' },
];

function fmtExp(expSec: number | undefined): string {
  if (!expSec) return 'unknown';
  const now = Math.floor(Date.now() / 1000);
  const deltaSec = expSec - now;
  const date = new Date(expSec * 1000).toISOString();
  if (deltaSec < 0) return `${date} (expired ${Math.abs(deltaSec)}s ago)`;
  const days = Math.round(deltaSec / 86400);
  return `${date} (in ~${days} days)`;
}

/** Keep the first 8 chars of an identifier so users pasting doctor output
 * to IT don't leak full user/org IDs, but we still have enough for triage. */
function truncateId(id: string | undefined): string {
  if (!id) return '(n/a)';
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

export async function runAuthDoctor(argv: string[]): Promise<void> {
  const env = getArgValue(argv, '--env');
  const profile = env ? envToProfile(env) : undefined;
  const apiUrlOverride = getArgValue(argv, '--api-url') ?? readBrandEnv('CANARY_API_URL');

  // Honor the `--org` / CANARY_ORG identity selector so `auth doctor --org X`
  // diagnoses X's session. Otherwise fall back to the --env / default profile.
  // Reuse the canonical precedence from auth.ts (don't re-derive it here).
  const orgSelector = getOrgSelector(argv, env);
  const envToken = readBrandEnv('CANARY_API_TOKEN');
  let stored = await readStoredAuth(profile);
  if (orgSelector) {
    const match = await findSessionByOrg(orgSelector, env);
    if (match.kind === 'ambiguous') {
      printAmbiguousOrg(orgSelector, match.candidates);
      process.exit(1);
    }
    if (match.kind !== 'match') {
      printNoSessionForOrg(orgSelector, await readAllProfiles());
      process.exit(1);
    }
    stored = match.auth;
  }
  // With an explicit --org the stored session's token wins; otherwise the API
  // token env var takes precedence (CI/automation), matching resolveConfig.
  const token = orgSelector ? (stored?.token ?? envToken) : (envToken ?? stored?.token);
  const apiUrl =
    apiUrlOverride ?? (env && ENV_URLS[env]?.api) ?? stored?.apiUrl ?? DEFAULT_API_URL;

  console.log('── canary auth doctor ──');
  console.log(`node:          ${process.version}`);
  console.log(`platform:      ${process.platform}`);
  const tokenFromStored = token && token === stored?.token;
  console.log(`api url:       ${apiUrl}`);
  console.log(`profile:       ${orgSelector ? `org "${orgSelector}"` : (profile ?? '(default)')}`);
  if (stored?.orgName) console.log(`org:           ${stored.orgName}`);
  console.log(
    `token source:  ${tokenFromStored ? 'auth.json' : token ? 'API token env var' : 'NONE'}`
  );
  console.log(
    `NODE_EXTRA_CA_CERTS: ${process.env.NODE_EXTRA_CA_CERTS ?? '(not set)'}`
  );
  console.log(`NODE_OPTIONS:        ${process.env.NODE_OPTIONS ?? '(not set)'}`);

  if (!token) {
    console.log('');
    console.log('No token found. Run: canary login' + (env ? ` --env ${env}` : ''));
    process.exit(1);
  }

  const payload = decodeJwtPayload(token);
  const exp = typeof payload?.exp === 'number' ? (payload.exp as number) : undefined;
  const sub = typeof payload?.sub === 'string' ? (payload.sub as string) : undefined;
  const orgId = typeof payload?.org_id === 'string' ? (payload.org_id as string) : undefined;
  console.log('');
  console.log('── token ──');
  console.log(`decoded:  ${payload ? 'ok' : 'UNDECODABLE (likely corrupted file)'}`);
  console.log(`sub:      ${truncateId(sub)}`);
  console.log(`org_id:   ${truncateId(orgId)}`);
  console.log(`exp:      ${fmtExp(exp)}`);

  console.log('');
  console.log('── probes ──');
  console.log('Each probe exercises a different path — mixed pass/fail here is the');
  console.log('signal for Zscaler URL-category or DLP filtering.');
  console.log('');

  let hadProxyInjection = false;
  let hadTls = false;
  let passed = 0;

  for (const probe of DEFAULT_PROBES) {
    const url = `${apiUrl}${probe.path}`;
    const result = await apiFetch(url, {
      method: probe.method ?? 'GET',
      headers: { Authorization: `Bearer ${token}` },
      token,
      // Short per-probe timeout so one stalled endpoint doesn't block diagnosis.
      timeoutMs: 10_000,
    });
    if (result.ok) {
      const rid = result.requestId ?? '(absent)';
      console.log(`  ✓ ${probe.name.padEnd(28)} 200  rid=${rid}`);
      passed += 1;
    } else {
      const f = result.failure;
      const rid = f.requestId ?? '(absent)';
      console.log(
        `  ✗ ${probe.name.padEnd(28)} ${f.status ?? 'ERR'}  [${f.kind}] rid=${rid}`
      );
      const firstLine = f.message.split('\n')[0];
      console.log(`      ${firstLine}`);
      if (f.kind === 'tls') hadTls = true;
      if (f.kind === 'html-body' || f.kind === 'proxy-injected-4xx') hadProxyInjection = true;
    }
  }

  console.log('');
  console.log('── diagnosis ──');
  if (hadTls) {
    console.log('TLS verification failed on at least one probe.');
    console.log('  → A corporate proxy is intercepting HTTPS. Trust the proxy root CA:');
    console.log('      export NODE_EXTRA_CA_CERTS=/path/to/corporate-root.pem');
    console.log('    or on Node 20+:');
    console.log('      export NODE_OPTIONS=--use-system-ca');
  } else if (hadProxyInjection) {
    console.log('At least one response was NOT from our API (HTML body or missing');
    console.log('x-canary-request-id). A proxy block page or captive portal is in the way.');
    console.log('  → Share this output with your IT/Zscaler admin and ask them to');
    console.log(`    allowlist ${apiUrl}.`);
  } else if (passed === DEFAULT_PROBES.length) {
    console.log('All probes reached our API successfully. Auth + connectivity look healthy.');
    console.log('If you are still seeing intermittent issues, re-run with --debug on the');
    console.log('failing command: CANARY_DEBUG=1 canary <cmd> …');
  } else if (passed > 0) {
    console.log('Mixed results: some probes reached our API, others did not.');
    console.log('  → This is the classic signature of URL-category filtering at the proxy');
    console.log('    (certain paths like /run, /execute are blocked while others pass)');
    console.log('    or partial domain allowlisting.');
    console.log('  → Share the probe table with your IT/Zscaler admin; include the exact');
    console.log('    failing paths and the "[kind]" column.');
  } else {
    console.log('Every probe failed. Check your network connection and proxy settings,');
    console.log('then retry with CANARY_DEBUG=1 canary auth doctor.');
  }

  if (passed < DEFAULT_PROBES.length) process.exit(1);
}
